import React, { useState, useEffect } from 'react';
import { Form, Button, Alert } from '@/components/ui';
import { workIdService, type WorkIdDetail } from '../../../services/workIdService';
import ResponsiveModal from '../../../components/ResponsiveModal';

interface WorkIdReopenModalProps {
  visible: boolean;
  onClose: () => void;
  workId: WorkIdDetail | null;
  onSuccess: () => void;
}

const WorkIdReopenModal: React.FC<WorkIdReopenModalProps> = ({
  visible,
  onClose,
  workId,
  onSuccess,
}) => {
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      setComments('');
      setError(null);
    }
  }, [visible, workId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!workId) return;

    setError(null);
    setSubmitting(true);

    try {
      await workIdService.reopen(workId.id, comments.trim() || undefined);
      onClose();
      onSuccess();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to reopen work ID');
    } finally {
      setSubmitting(false);
    }
  };

  if (!workId) return null;

  const title = `Reopen Work ID - ${workId.workIdentificationId ?? workId.id}`;
  const fromClosedWithoutAction = workId.status?.toUpperCase() === 'CLOSED_WITHOUT_ACTION';

  return (
    <ResponsiveModal
      isOpen={visible}
      onClose={onClose}
      title={title}
      size="medium"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Close
          </Button>
          <Button
            type="button"
            variant="success"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? 'Reopening…' : 'Reopen Work ID'}
          </Button>
        </>
      }
    >
      <Form onSubmit={handleSubmit}>
        {error && (
          <Alert variant="danger">
            {error}
          </Alert>
        )}

        <p className="tw-text-content-muted tw-mb-3">
          {fromClosedWithoutAction ? (
            <>
              Status will return to <strong>Approved</strong>. You will be recorded as the approving user.
            </>
          ) : (
            <>
              Status will return to <strong>New</strong> and the Work ID will require approval again.
            </>
          )}
        </p>

        <Form.Group className="tw-mb-0">
          <Form.Label>Comment (optional)</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder="e.g. Reopening for re-evaluation"
          />
        </Form.Group>
      </Form>
    </ResponsiveModal>
  );
};

export default WorkIdReopenModal;
